/**
 * Modal dialog for changing the user's password.
 * Provides new-password and confirm fields with eye toggles and
 * real-time validation matching the Signup page patterns.
 *
 * @module components/ChangePasswordModal
 */

import React, { useState }       from 'react';
import { Eye, EyeOff }           from 'lucide-react';
import Modal                     from './Modal';
import { useI18n }               from '../dataProviders/I18nProvider';
import apiClient                 from '../procedural/apiClient';
import { isPasswordComplex }     from '../procedural/passwordValidation';
import * as constants            from '../constants';

/**
 * Renders a change-password modal dialog.
 *
 * @param {object}   props
 * @param {Function} props.onClose   - Called to dismiss the modal.
 * @param {Function} props.onSuccess - Called after a successful password change.
 * @returns {React.ReactElement}
 */
export default function ChangePasswordModal({ onClose, onSuccess }) {
  const { t } = useI18n();

  const [password, setPassword]                        = useState('');
  const [passwordConfirm, setPasswordConfirm]          = useState('');
  const [showPassword, setShowPassword]                = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm]  = useState(false);
  const [error, setError]                              = useState(null);
  const [submitting, setSubmitting]                    = useState(false);

  const passwordValid  = password.length > 0 && isPasswordComplex(password);
  const passwordsMatch = passwordConfirm.length > 0 && password === passwordConfirm;

  /**
   * Submit the password change.
   *
   * @param {React.FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!password || !passwordConfirm) {
      setError(t('settings.changePassword.error.missingFields'));
      return;
    }
    if (!isPasswordComplex(password)) {
      setError(t('settings.changePassword.error.passwordComplexity'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('settings.changePassword.error.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/settings/change-password', { password });
      onSuccess();
    } catch {
      setError(t('settings.changePassword.error.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.changePassword.title')} onClose={onClose}>
      <form className="auth-form" onSubmit={handleSubmit}>
        {error && <div className="auth-error">{error}</div>}

        {/* New Password */}
        <div className="auth-field">
          <label className="auth-label" htmlFor="cp-password">
            {t('settings.changePassword.password.label')}
          </label>
          <div className="auth-input-wrapper">
            <input
              id="cp-password"
              className="auth-input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              maxLength={constants.MAX_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              className="auth-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <span className="auth-hint">
            {t('signup.passwordRequirements')}
          </span>
          {password.length > 0 && (
            <span className={passwordValid ? 'auth-validity-icon--valid' : 'auth-validity-icon--invalid'}
                  style={{ fontSize: '12px' }}>
              {passwordValid
                ? t('signup.passwordValid')
                : t('signup.passwordInvalid')}
            </span>
          )}
        </div>

        {/* Confirm Password */}
        <div className="auth-field">
          <label className="auth-label" htmlFor="cp-password-confirm">
            {t('settings.changePassword.passwordConfirm.label')}
          </label>
          <div className="auth-input-wrapper">
            <input
              id="cp-password-confirm"
              className="auth-input"
              type={showPasswordConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              maxLength={constants.MAX_PASSWORD_LENGTH}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              className="auth-toggle-password"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
              aria-label={showPasswordConfirm ? 'Hide password' : 'Show password'}
            >
              {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {passwordConfirm.length > 0 && (
            <span className={passwordsMatch ? 'auth-validity-icon--valid' : 'auth-validity-icon--invalid'}
                  style={{ fontSize: '12px' }}>
              {passwordsMatch
                ? t('signup.passwordMatch')
                : t('signup.passwordMismatch')}
            </span>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
            {t('settings.changePassword.cancel')}
          </button>
          <button type="submit" className="modal-btn modal-btn--primary" disabled={submitting}>
            {t('settings.changePassword.ok')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
